import { Component, input, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { CardModule } from 'primeng/card';
import { TooltipModule } from 'primeng/tooltip';
import { ProjectResponseDashboardDto } from '../../api/models/project-response-dashboard-dto';

export interface InvestmentRound {
  type: 'private' | 'public' | 'tge';
  isLive: boolean;
  price: string;
  startDate: string;
  endDate: string;
}

@Component({
  selector: 'app-investment-rounds-card',
  standalone: true,
  imports: [CommonModule, TranslateModule, CardModule, TooltipModule],
  templateUrl: './investment-rounds-card.component.html',
  styleUrl: './investment-rounds-card.component.css',
})
export class InvestmentRoundsCardComponent {
  project = input.required<ProjectResponseDashboardDto>();

  // Computed investment rounds based on project data
  protected readonly rounds = computed(() => {
    const project = this.project();
    if (!project) return [];

    const now = new Date();

    // Determine which round is currently live
    const isPrivateSaleLive =
      project.privateSaleStart && project.privateSaleEnd
        ? now >= new Date(project.privateSaleStart) && now <= new Date(project.privateSaleEnd)
        : false;

    const isPublicSaleLive =
      project.publicSaleStart && project.publicSaleEnd
        ? now >= new Date(project.publicSaleStart) && now <= new Date(project.publicSaleEnd)
        : false;

    const isTGELive = project.tgeDate ? now >= new Date(project.tgeDate) : false;

    // Helper function to format date
    const formatDate = (dateString?: string): string => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    };

    // Build rounds array from actual backend data
    const rounds: InvestmentRound[] = [];

    // Private Sale Round
    if (project.privateSaleStart || project.privateSaleEnd) {
      rounds.push({
        type: 'private' as const,
        isLive: isPrivateSaleLive,
        price: project.privateSalePrice
          ? `1 USDT = ${project.privateSalePrice} ${project.tokenSymbol || 'tokens'}`
          : 'Price TBA',
        startDate: formatDate(project.privateSaleStart),
        endDate: formatDate(project.privateSaleEnd),
      });
    }

    // Public Sale Round
    if (project.publicSaleStart || project.publicSaleEnd) {
      rounds.push({
        type: 'public' as const,
        isLive: isPublicSaleLive,
        price: project.publicSalePrice
          ? `1 USDT = ${project.publicSalePrice} ${project.tokenSymbol || 'tokens'}`
          : 'Price TBA',
        startDate: formatDate(project.publicSaleStart),
        endDate: formatDate(project.publicSaleEnd),
      });
    }

    // TGE Date
    if (project.tgeDate) {
      rounds.push({
        type: 'tge' as const,
        isLive: isTGELive,
        price: project.tgePrice
          ? `1 USDT = ${project.tgePrice} ${project.tokenSymbol || 'tokens'}`
          : 'Price TBA',
        startDate: formatDate(project.tgeDate),
        endDate: '',
      });
    }

    return rounds;
  });
}
